'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Users } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { FormInput } from '@/components/forms/FormInput';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormDatePicker } from '@/components/forms/FormDatePicker';
import { FormPhoneInput } from '@/components/forms/FormPhoneInput';
import { ImageUpload } from '@/components/common/ImageUpload';

const profileDetailsSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').optional(),
  dateOfBirth: z
    .date({
      invalid_type_error: 'Please select a valid date',
    })
    .max(new Date(), 'Date of birth cannot be in the future')
    .refine((date) => {
      const today = new Date();
      const thirteenYearsAgo = new Date(
        today.getFullYear() - 13,
        today.getMonth(),
        today.getDate()
      );
      return date <= thirteenYearsAgo;
    }, 'You must be at least 13 years old')
    .optional(),
  gender: z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.enum(['male', 'female', 'other', 'prefer-not-to-say']).optional()),
  phoneNumber: z.string().min(10, 'Please enter a valid phone number'),
  profilePicture: z.string().optional(),
});

export type ProfileDetailsData = z.infer<typeof profileDetailsSchema> & {
  countryCode?: string;
  mobileNumber?: string;
};

interface ProfileDetailsFormProps {
  onSubmit: (data: ProfileDetailsData) => void;
  onSkip: () => void;
  isLoading?: boolean;
  initialData?: Partial<ProfileDetailsData>;
  existingUserData?: {
    name?: string;
    dateOfBirth?: string;
    gender?: string;
    phoneNumber?: string;
    profilePicture?: string;
    countryCode?: string;
    mobileNumber?: string;
  };
}

const genderOptions = [
  { value: '', label: 'Select gender' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer-not-to-say', label: 'Prefer not to say' },
];

export function ProfileDetailsForm({
  onSubmit,
  onSkip,
  isLoading = false,
  initialData,
  existingUserData,
}: ProfileDetailsFormProps) {
  const [profileImage, setProfileImage] = useState<string | undefined>(
    existingUserData?.profilePicture || initialData?.profilePicture
  );

  // Store country code and mobile number separately
  const [phoneData, setPhoneData] = useState<{
    countryCode?: string;
    mobileNumber?: string;
  }>({
    countryCode: existingUserData?.countryCode,
    mobileNumber: existingUserData?.mobileNumber,
  });

  // Check if user already has required data
  const hasRequiredData = existingUserData?.phoneNumber;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    trigger,
  } = useForm<ProfileDetailsData>({
    resolver: zodResolver(profileDetailsSchema),
    mode: 'onChange', // Enable real-time validation
    defaultValues: {
      name: existingUserData?.name || initialData?.name || '',
      dateOfBirth: existingUserData?.dateOfBirth
        ? new Date(existingUserData.dateOfBirth)
        : initialData?.dateOfBirth,
      gender: (existingUserData?.gender || initialData?.gender || '') as any,
      phoneNumber:
        existingUserData?.phoneNumber || initialData?.phoneNumber || '',
      profilePicture:
        existingUserData?.profilePicture || initialData?.profilePicture,
    },
  });

  const currentName = watch('name');

  const handleFormSubmit = async (data: ProfileDetailsData) => {
    // Always allow submission if user has existing required data (phone number)
    // For new users, only require phone number - DOB and gender are truly optional
    if (!hasRequiredData && !data.phoneNumber) {
      const isFormValid = await trigger(['phoneNumber']);
      if (!isFormValid) {
        return;
      }
    }

    onSubmit({
      ...data,
      profilePicture: profileImage,
      countryCode: phoneData.countryCode,
      mobileNumber: phoneData.mobileNumber,
    });
  };

  // Calculate if submit should be enabled - only require phone number
  const phoneNumber = watch('phoneNumber');
  const canSubmit =
    hasRequiredData || (phoneNumber && phoneNumber.length >= 10);

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="space-y-4"
      noValidate
    >
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900">
          {hasRequiredData ? 'Verify Your Details' : 'Complete Your Profile'}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {hasRequiredData
            ? 'Please verify and update your information if needed'
            : 'Help tournament organizers get to know you better'}
        </p>
      </div>

      <div className="flex justify-center">
        <ImageUpload
          initialImage={profileImage}
          tag="player"
          onChange={setProfileImage}
          onError={() => {}}
          shape="circle"
          fallbackText={currentName || 'Player'}
        />
      </div>

      <div className="space-y-4">
        <FormInput
          label="Full Name"
          name="name"
          register={register}
          error={errors.name?.message}
          leftIcon={<User className="h-4 w-4 text-gray-500" />}
          placeholder="Enter your full name"
        />

        <FormPhoneInput
          label="Phone Number *"
          name="phoneNumber"
          control={control}
          error={errors.phoneNumber?.message}
          placeholder="Enter your phone number"
          onCountryChange={(countryCode, countryData, formattedValue) => {
            // Extract the mobile number without country code
            const phone = formattedValue.replace(/\D/g, ''); // Remove all non-digits
            const dialCode = countryData.dialCode;
            const mobileNumber = phone.startsWith(dialCode)
              ? phone.substring(dialCode.length)
              : phone;

            setPhoneData({
              countryCode,
              mobileNumber,
            });
          }}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormDatePicker
            label="Date of Birth (Optional)"
            name="dateOfBirth"
            control={control}
            error={errors.dateOfBirth?.message}
            placeholder="Select your date of birth"
            showYearDropdown={true}
            maxDate={new Date()}
            minDate={new Date(1950, 0, 1)}
          />

          <FormSelect
            label="Gender (Optional)"
            name="gender"
            register={register}
            error={errors.gender?.message}
            options={genderOptions}
            leftIcon={<Users className="h-4 w-4 text-gray-500" />}
          />
        </div>
      </div>

      <div className="flex justify-between space-x-3 border-t border-gray-200 pt-4">
        {hasRequiredData && (
          <Button
            type="button"
            variant="light"
            onClick={onSkip}
            disabled={isLoading || isSubmitting}
          >
            Keep Current Info
          </Button>
        )}
        <Button
          type="submit"
          isLoading={isLoading || isSubmitting}
          disabled={!canSubmit}
          className="flex-1"
        >
          {hasRequiredData ? 'Update & Continue' : 'Continue'}
        </Button>
      </div>
    </form>
  );
}
